import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import JourneyDemo from '../JourneyDemo/JourneyDemo';
import * as ROUTES from '../../../constants/routes';

const IconBolt = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>;
const IconTrend = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>;
const IconClock = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;
const IconBars = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>;
const IconShield = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>;
const IconUsers = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
const IconAlert = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>;

const TABS = [
    {
        key: 'investment', label: 'Investment Ready', img: require("../../../../images/landing-pages/VC/diligence-pack.png"), width: '50%',
        h: 'Arrive with the pack that lets an investor say yes.',
        p: 'Most founders invest months on the pitch and days on the corporate picture. Diligentsia fixes the asymmetry — governance, IP, cap table, financials, all structured before you need them.',
        feats: [
            { Icon: IconBolt, t: 'Close your round faster', d: 'Structured packs cut information requests. Rounds stall on paperwork, not the business case.' },
            { Icon: IconTrend, t: 'Protect your valuation', d: 'A 10% uplift on a £1m round is £100k in your pocket. On a £5m round, £500k.' },
            { Icon: IconClock, t: 'Start before the call comes', d: "Build it now. When interest arrives, you're ready in hours, not weeks." },
        ],
    },
    {
        key: 'growth', label: 'Growth Ready', img: require("../../../../images/landing-pages/companies/growth-ready-icon.png"), width: '100%',
        h: "Let's now help you turbo boost your growth and value",
        p: 'Diligentsia provides you a set of tools to systematically go through your business and quickly improve and create value.',
        feats: [
            { Icon: IconBars, t: '20x Healthboost Modules', d: 'Use our AI capabilities to systematically identify areas of value add across your business' },
            { Icon: IconShield, t: 'Fix commercial gaps early ', d: 'Use our 200+ specialist AI agents to analyse your business across 20 areas and identify gaps and weaknesses. And then we help you fix them.' },
            { Icon: IconUsers, t: 'Harness our specialist partners', d: 'AdvisorMatch with 80+ advisors, or the AppStore partners integrated into our paltform.' },
        ],
    },
    {
        key: 'exit', label: 'Exit Ready', img: require("../../../../images/landing-pages/companies/exit-ready-icon.png"), width: '100%',
        h: "Don’t allow your exit value to be chipped by 10%.",
        p: "Most exits don't fail. They get chipped — by surprises that were entirely preventable.",
        callout: { big: '£1,000,000', text: 'value of a 10% improvement in exit preparation on a £10M sale. Against £1,000/year — one of the highest-return decisions a founder can make.' },
        iconVariant: 'alert',
        feats: [
            { Icon: IconAlert, t: 'Use our 200 AI agents to get you exit ready', d: 'Comprehensive diligence pack, research reports, and detailed gap analysis' },
            { Icon: IconAlert, t: 'Use the AI diligence tools we provide your buyers', d: 'Reverse diligence yourself with the ai diligence tools we offer buyers before you go out to market' },
            { Icon: IconAlert, t: 'You can now go out to the market with confidence', d: 'You should now be in a position to push back on chipping' },
        ],
    },
];

// The demo is a scroll stop like the other three, but has no TABS entry — it renders the
// interactive walkthrough instead of the copy/art panel.
const DEMO_KEY = 'demo';
const KEYS = [...TABS.map((t) => t.key), DEMO_KEY];

const CompaniesLifecycleSection = () => {
    const [active, setActive] = useState('investment');
    const [progress, setProgress] = useState(0); // 0..1 through the scroll wrapper
    const wrapRef = useRef(null);

    const compute = useCallback(() => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        const wTop = wrap.getBoundingClientRect().top + window.scrollY;
        const range = wrap.offsetHeight - window.innerHeight;
        if (range <= 0) { setProgress(0); return; }
        const scrolled = Math.max(0, Math.min(window.scrollY - wTop, range));
        const p = scrolled / range;
        setProgress(p);
        // Four evenly-spaced stops — the demo is the last one, not a special case.
        const key = p >= 0.75 ? 'demo' : p >= 0.5 ? 'exit' : p >= 0.25 ? 'growth' : 'investment';
        setActive((prev) => {
            if (prev === key) return prev;
            // Reason: replaceState rather than a router navigation — scrolling through the stops
            // would otherwise push an entry per stop and hijack the back button.
            const wanted = key === DEMO_KEY ? ROUTES.COMPANIES_DEMO_HASH : '';
            if (window.location.hash !== wanted) {
                window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${wanted}`);
            }
            return key;
        });
    }, []);

    // Arriving with the demo hash jumps straight to that stop. Runs once the wrapper has a height,
    // so the scroll target is real rather than 0.
    useEffect(() => {
        if (window.location.hash !== ROUTES.COMPANIES_DEMO_HASH) return;
        let raf = 0;
        const jump = () => {
            const wrap = wrapRef.current;
            if (!wrap || wrap.offsetHeight - window.innerHeight <= 0) { raf = requestAnimationFrame(jump); return; }
            const wTop = wrap.getBoundingClientRect().top + window.scrollY;
            const range = wrap.offsetHeight - window.innerHeight;
            const target = KEYS.indexOf(DEMO_KEY) / (KEYS.length - 1);
            // 'auto': a smooth scroll from the top of a long page is a slow, disorienting ride.
            window.scrollTo({ top: wTop + target * range, behavior: 'auto' });
        };
        raf = requestAnimationFrame(jump);
        return () => cancelAnimationFrame(raf);
    }, []);

    useEffect(() => {
        compute();
        window.addEventListener('scroll', compute, { passive: true });
        window.addEventListener('resize', compute, { passive: true });
        return () => {
            window.removeEventListener('scroll', compute);
            window.removeEventListener('resize', compute);
        };
    }, [compute]);

    const goTo = (key) => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        const wTop = wrap.getBoundingClientRect().top + window.scrollY;
        const range = wrap.offsetHeight - window.innerHeight;
        const idx = KEYS.indexOf(key);
        const target = idx <= 0 ? 0 : idx / (KEYS.length - 1);
        window.scrollTo({ top: wTop + target * range, behavior: 'smooth' });
    };

    const tab = TABS.find((t) => t.key === active) || TABS[TABS.length - 1];
    const barWidth = Math.max(4, Math.round(progress * 100));

    return (
        <div id="lifecycle" className="lp-life-scroll" ref={wrapRef}>
            <section className="lp-life-sticky">
                <div className="lp-life-bar">
                    <div className="lp-life-tabs" role="tablist">
                        {TABS.map((t) => (
                            <button key={t.key} role="tab" className={`lp-life-tab ${t.key === active ? 'is-active' : ''}`} onClick={() => goTo(t.key)}>{t.label}</button>
                        ))}
                        <button
                            type="button"
                            role="tab"
                            className={`lp-life-tab ${active === DEMO_KEY ? 'is-active' : ''}`}
                            onClick={() => goTo(DEMO_KEY)}
                        >2-minute demo</button>
                    </div>
                    <div className="lp-life-track"><div className="lp-life-progress" style={{ width: barWidth + '%' }} /></div>
                </div>
                {active === DEMO_KEY ? (
                    <div className="lp-life-content lp-life-demo"><JourneyDemo /></div>
                ) : (
                <div className="lp-life-content">
                    <div className="lp-life-panel" key={active}>
                        <div className="c">
                            <h2>{tab.h}</h2>
                            <p className="lead">{tab.p}</p>
                            {tab.callout && (
                                <div className="lp-life-callout">
                                    <div className="big">{tab.callout.big}</div>
                                    <p>{tab.callout.text}</p>
                                </div>
                            )}
                            <div className="lp-life-feats">
                                {tab.feats.map(({ Icon, t, d }, i) => (
                                    <div className="lp-life-feat" key={i}>
                                        <span className={`lp-fi ${tab.iconVariant === 'alert' ? 'lp-fi--alert' : ''}`}><Icon /></span>
                                        <div><div className="ft">{t}</div><div className="fd">{d}</div></div>
                                    </div>
                                ))}
                            </div>
                            <Link className="lp-btn lp-btn--primary" to={`${ROUTES.PRICING_PLAN}?journey=${active}#whats-included`}>See what's included</Link>
                        </div>
                        <div className="lp-life-art">
                            <img src={tab.img} alt={tab.label} style={{ width: tab.width, marginTop: tab.label === 'Investment Ready' ? '100px' : '0' }} />
                        </div>
                    </div>
                </div>
                )}
            </section>
        </div>
    );
};

export default CompaniesLifecycleSection;
